import React, { useMemo } from "react";
import { useTranslation } from "react-i18next"; // Keep for 't' function
import VariationSelector from "../../common/VariationSelector"; // Import the new component
import DetailsActions from "../../common/DetailsActions";
import ProductInformation from "./ProductInformation";
import ProductMeta from "./ProductMeta";
import type {
  TransformedProduct,
  TransformedVariation,
} from "../../../types/productApiTypes";
import type { SelectedAttributeSlugs } from "../../../hooks/useProductVariationSelector";

// --- Component Props ---
interface ProductDetailsProps {
  // Main required prop
  product: TransformedProduct;
  // Variation related props
  variations?: TransformedVariation[]; // Still useful for potentially listing all options if needed
  variationsLoading?: boolean; // This prop now includes translation readiness from parent
  selectedVariation: TransformedVariation | null;
  selectedAttributes: SelectedAttributeSlugs; // Expects { 'pa_color': 'blue', ... }
  onAttributeSelect: (attributeName: string, valueSlug: string) => void;
  // Removed productCategories & productTags props
}

// --- Component Implementation ---
const ProductDetails: React.FC<ProductDetailsProps> = ({
  product,
  variationsLoading = false, // This prop now includes translation readiness from parent
  selectedVariation = null,
  selectedAttributes = {},
  onAttributeSelect,
}) => {
  const { t } = useTranslation(); // Keep for 't' function

  // --- Extract Data from Product Prop ---
  const {
    name: productName,
    shortDescription,
    enrichedAttributes = [], // Default to empty array
    type: productType,
  } = product;

  // --- Constants & Derived Values ---
  const isVariable = productType === "variable";

  // Use the loading state passed from the parent directly
  const isLoading = variationsLoading;

  // --- Calculated States ---
  const hasRequiredAttributes = useMemo(() => {
    if (!isVariable) return true;
    return enrichedAttributes
      .filter((attr) => attr.hasVariations && attr.taxonomy)
      .every((attr) => !!selectedAttributes[attr.taxonomy!]);
  }, [isVariable, enrichedAttributes, selectedAttributes]);

  const isCombinationAvailable = !!selectedVariation;

  return (
    <div className="product-details">
      {/* Product Information */}
      <ProductInformation
        productName={productName}
        shortDescription={shortDescription}
        variationsLoading={isLoading} // Pass combined loading state down
      />

      {/* Variations Section - Replaced with VariationSelector */}
      {isVariable && (
        <VariationSelector
          enrichedAttributes={enrichedAttributes}
          selectedAttributes={selectedAttributes}
          onAttributeSelect={onAttributeSelect}
          variationsLoading={isLoading} // Pass combined loading state down
          containerClassName="variations" // Pass specific class for ProductDetails context
          rowClassName="variation-row" // Pass specific class for ProductDetails context
        />
      )}

      {/* Variation Notices */}
      {isVariable && !isLoading && !hasRequiredAttributes && (
        <div className="variation-notice">
          {t("product.pleaseSelectOptions")} {/* Translate */}
        </div>
      )}
      {isVariable &&
        hasRequiredAttributes &&
        !isLoading &&
        !isCombinationAvailable && (
          <div className="variation-required-notice">
            {t("product.combinationUnavailable")} {/* Translate */}
          </div>
        )}

      {/* Add to Cart Form - Use the REFACTORED DetailsActions */}
      {/* For the single product page, we pass showViewDetailsButton={false} (the default) so nothing renders */}
      <DetailsActions
        product={product}
        containerClassName="single-product-actions"
      />
    </div>
  );
};

export default ProductDetails;
